import type { ReactElement, ReactNode } from 'react'
import {
	AlertCircleIcon,
	CheckCircleIcon,
	ClockIcon,
	XCircleIcon
} from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/helpers/utils'

type PhotosetFinalStatus = 'success' | 'error' | 'warning' | 'pending'

const PhotosetFinalIcon = ({
	icon,
	status
}: {
	icon?: ReactNode
	status?: PhotosetFinalStatus
}) => {
	if (icon) {
		return icon
	}
	if (status === 'error') {
		return <XCircleIcon className='fill-danger-600' size={40} />
	}
	if (status === 'warning') {
		return <AlertCircleIcon className='fill-warning-600' size={40} />
	}
	if (status === 'pending') {
		return <ClockIcon className='fill-warning-600' size={40} />
	}
	return (
		<CheckCircleIcon className='fill-success-600' height={48} width={48} />
	)
}

/**
 * Small pill under the subtitle, used to surface a deadline or a permission
 * that changes what the actions below mean (e.g. "review window closes in 46h").
 */
export const PhotosetFinalBadge = ({
	children,
	tone = 'neutral'
}: {
	children: ReactNode
	tone?: 'neutral' | 'warning' | 'success'
}) => {
	const dotClassName = {
		neutral: 'bg-neutral-500',
		warning: 'bg-warning-600',
		success: 'bg-success-600'
	}[tone]

	return (
		<span className='inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-2.5 py-1.5 text-neutral-900 text-xs/4'>
			<span className={cn('size-2 rounded-full', dotClassName)} />
			{children}
		</span>
	)
}

export const PhotosetFinal = ({
	title,
	subtitle,
	status,
	badge,
	endSlot,
	icon,
	isLoading,
	className
}: {
	title: string | ReactElement
	subtitle: string | ReactElement
	status?: PhotosetFinalStatus
	/** Pill rendered between the subtitle and the actions. */
	badge?: ReactNode
	icon?: ReactNode
	endSlot?: ReactNode
	isLoading?: boolean
	className?: string
}) => {
	return (
		<div className='flex h-full w-full flex-col items-center justify-center'>
			{isLoading ? (
				<Spinner className='fill-neutral-500' size={48} />
			) : (
				<>
					<PhotosetFinalIcon icon={icon} status={status} />
					<div
						className={cn(
							'mx-auto mt-8 mb-6 flex w-full max-w-141 flex-col items-center gap-6 text-center',
							className
						)}
					>
						<div className='flex flex-col items-center gap-2'>
							<h1 className='font-bold text-display-xs-fixed text-neutral-900'>
								{title}
							</h1>
							<p className='text-neutral-900 text-xl'>
								{subtitle}
							</p>
							{badge}
						</div>
						{endSlot}
					</div>
				</>
			)}
		</div>
	)
}
